/**
 * Lead Identity Resolution & Persistence
 * =======================================
 * Captures and resolves lead identity for the Guided Consultation quiz.
 * Does NOT create Supabase Auth sessions — uses localStorage/sessionStorage only.
 */

import { supabase } from '@/integrations/supabase/client';

// ─── Types ──────────────────────────────────────────────────────────────────

export interface LeadIdentity {
  full_name: string;
  email: string;
  phone: string; // E.164 normalized
  session_id: string;
  captured_at: string; // ISO
  source: 'guided_consultation_entry' | 'anonymous';
}

// ─── Constants ──────────────────────────────────────────────────────────────

const STORAGE_KEY = 'nathof_lead_identity';
const SESSION_ID_KEY = 'nathof_consultation_session_id';

// ─── Helpers ────────────────────────────────────────────────────────────────

export function generateUUID(): string {
  return crypto.randomUUID?.() ?? `${Date.now()}-${Math.random().toString(36).slice(2, 11)}`;
}

export function getConsultationSessionId(): string {
  let id = sessionStorage.getItem(SESSION_ID_KEY);
  if (!id) {
    id = localStorage.getItem(SESSION_ID_KEY);
  }
  if (!id) {
    id = generateUUID();
  }
  sessionStorage.setItem(SESSION_ID_KEY, id);
  localStorage.setItem(SESSION_ID_KEY, id);
  return id;
}

export async function sha256(value: string): Promise<string> {
  const encoder = new TextEncoder();
  const data = encoder.encode(value.toLowerCase().trim());
  const hashBuffer = await crypto.subtle.digest('SHA-256', data);
  return Array.from(new Uint8Array(hashBuffer))
    .map((b) => b.toString(16).padStart(2, '0'))
    .join('');
}

// ─── Identity Resolution ────────────────────────────────────────────────────

/**
 * Checks sessionStorage → localStorage → Supabase Auth (read-only).
 * Returns existing identity or null.
 */
export function ensureLeadIdentity(): LeadIdentity | null {
  // 1. sessionStorage
  try {
    const session = sessionStorage.getItem(STORAGE_KEY);
    if (session) {
      const parsed = JSON.parse(session) as LeadIdentity;
      if (parsed.email && parsed.full_name) return parsed;
    }
  } catch { /* ignore */ }

  // 2. localStorage
  try {
    const local = localStorage.getItem(STORAGE_KEY);
    if (local) {
      const parsed = JSON.parse(local) as LeadIdentity;
      if (parsed.email && parsed.full_name) {
        // Re-hydrate sessionStorage
        sessionStorage.setItem(STORAGE_KEY, local);
        return parsed;
      }
    }
  } catch { /* ignore */ }

  // 3. Supabase Auth email (read-only, sync check via cached session)
  // We don't await here — just check if there's a cached session
  try {
    const sessionStr = localStorage.getItem('sb-ajkvzhvrmnkfuraahtqe-auth-token');
    if (sessionStr) {
      const authData = JSON.parse(sessionStr);
      const email = authData?.user?.email;
      const name = authData?.user?.user_metadata?.full_name || authData?.user?.user_metadata?.name;
      if (email) {
        const identity: LeadIdentity = {
          full_name: name || '',
          email,
          phone: '',
          session_id: getConsultationSessionId(),
          captured_at: new Date().toISOString(),
          source: 'guided_consultation_entry',
        };
        saveLeadIdentity(identity);
        return identity;
      }
    }
  } catch { /* ignore */ }

  return null;
}

// ─── Persistence ────────────────────────────────────────────────────────────

export function saveLeadIdentity(identity: LeadIdentity): void {
  const json = JSON.stringify(identity);
  sessionStorage.setItem(STORAGE_KEY, json);
  localStorage.setItem(STORAGE_KEY, json);

  // Also populate legacy keys for backward compatibility
  localStorage.setItem('nathof_quiz_email', identity.email);
  localStorage.setItem('nathof_lead_email', identity.email);
  localStorage.setItem('nathof_quiz_lead_name', identity.full_name);
}

// ─── DB Insert (fire-and-forget) ────────────────────────────────────────────

export async function insertConsultationLead(identity: LeadIdentity): Promise<void> {
  try {
    const [emailHash, phoneHash] = await Promise.all([
      sha256(identity.email),
      identity.phone ? sha256(identity.phone) : Promise.resolve(null),
    ]);

    await supabase.from('guided_consultation_leads').insert({
      session_id: identity.session_id,
      full_name: identity.full_name,
      email: identity.email,
      phone: identity.phone || null,
      email_hash: emailHash,
      phone_hash: phoneHash,
      source: identity.source,
    });
  } catch (err) {
    if (import.meta.env.DEV) {
      console.warn('[LeadIdentity] DB insert failed:', err);
    }
  }
}

// ─── Funnel Flag Updates (fire-and-forget) ──────────────────────────────────

export function updateConsultationLead(
  sessionId: string | null,
  field: 'quiz_started' | 'quiz_completed' | 'checkout_started' | 'purchase_completed',
): void {
  if (!sessionId) return;
  supabase
    .from('guided_consultation_leads')
    .update({ [field]: true })
    .eq('session_id', sessionId)
    .then(({ error }) => {
      if (error && import.meta.env.DEV) {
        console.warn(`[LeadIdentity] update ${field} failed:`, error.message);
      }
    });
}

// ─── DataCrazy Sync (fire-and-forget) ───────────────────────────────────────

export function syncLeadToDataCrazy(identity: LeadIdentity): void {
  const projectId = import.meta.env.VITE_SUPABASE_PROJECT_ID;
  const endpoint = `https://${projectId}.supabase.co/functions/v1/crm-identify`;
  const anonKey = import.meta.env.VITE_SUPABASE_PUBLISHABLE_KEY;

  fetch(endpoint, {
    method: 'POST',
    headers: {
      'Content-Type': 'application/json',
      'apikey': anonKey,
      'Authorization': `Bearer ${anonKey}`,
    },
    body: JSON.stringify({
      external_id: identity.session_id,
      lead: {
        name: identity.full_name,
        email: identity.email,
        phone: identity.phone,
      },
      context: {
        origin: 'consulta_guiada',
        source: identity.source,
      },
    }),
  }).catch((err) => {
    if (import.meta.env.DEV) {
      console.warn('[LeadIdentity] DataCrazy sync failed:', err);
    }
  });
}

// ─── Get current session_id (if identity exists) ────────────────────────────

export function getCurrentLeadSessionId(): string | null {
  const identity = ensureLeadIdentity();
  return identity?.session_id ?? sessionStorage.getItem(SESSION_ID_KEY) ?? localStorage.getItem(SESSION_ID_KEY);
}

/**
 * Alias for quiz_leads funnel — reads session_id from 'nathof_lead_identity'.
 */
export function getLeadSessionId(): string | null {
  try {
    const raw = localStorage.getItem('nathof_lead_identity');
    if (raw) {
      const identity = JSON.parse(raw);
      return identity?.session_id || null;
    }
  } catch { /* ignore */ }
  return null;
}

// ─── CRM Retry Logic ────────────────────────────────────────────────────────

/**
 * Attempts a single retry of a failed crm-identify call.
 * Reads payload from localStorage 'nathof_crm_retry'.
 * On success: removes retry flag, stores lead_id. On failure: silent.
 */
export function attemptCrmRetry(): void {
  try {
    const raw = localStorage.getItem('nathof_crm_retry');
    if (!raw) return;

    const payload = JSON.parse(raw);
    if (!payload?.external_id) return;

    supabase.functions.invoke('crm-identify', { body: payload })
      .then(({ data: result }) => {
        localStorage.removeItem('nathof_crm_retry');

        if (result?.lead_id) {
          try {
            const stored = JSON.parse(localStorage.getItem('nathof_lead_identity') || '{}');
            stored.datacrazy_lead_id = result.lead_id;
            localStorage.setItem('nathof_lead_identity', JSON.stringify(stored));
          } catch { /* ignore */ }

          const sessionId = payload.external_id;
          if (sessionId) {
            supabase.from('quiz_leads')
              .update({ datacrazy_lead_id: result.lead_id })
              .eq('session_id', sessionId)
              .then(() => {});
          }
        }
      })
      .catch(() => {
        // Leave retry flag for next attempt
      });
  } catch { /* ignore */ }
}

// ─── DEV-only Funnel State Logging ──────────────────────────────────────────

export function logLeadFunnelState(): void {
  if (!import.meta.env.DEV) return;
  console.log('[LeadFunnel] State:', {
    identity: localStorage.getItem('nathof_lead_identity'),
    crmRetry: localStorage.getItem('nathof_crm_retry'),
    quizProgress: localStorage.getItem('nathof_quiz_progress'),
  });
}
